import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import { deriveStatus, errMsg, fmtQty, lineGap, lineTotals, STATUS_STYLES } from "./mcfUtils";


// Admin-only cumulative GRN update. Only changed lines are sent; status follows pending qty.
export function GrnUpdateDialog({ transfer, onClose, onSaved }) {
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLines((transfer?.items || []).map((i) => ({ ...i, grn_input: String(i.grn_qty ?? 0) })));
  }, [transfer]);

  if (!transfer) return null;

  const set = (idx, patch) => setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const view = lines.map((l) => ({ ...l, grn_qty: Number(l.grn_input) || 0 }));
  const t = lineTotals(view);
  const nextStatus = deriveStatus(view);

  const errors = view.map((l) => {
    if (l.grn_input === "" || Number.isNaN(Number(l.grn_input))) return "Enter a number";
    if (l.grn_qty < 0) return "Cannot be negative";
    if (l.grn_qty > l.sent_qty && !l.discrepancy_approved) return "Exceeds sent — approve excess";
    return null;
  });

  const save = async () => {
    if (errors.some(Boolean)) return toast.error("Fix the highlighted GRN quantities");
    const changed = view.filter((l, i) => l.grn_qty !== Number(transfer.items[i].grn_qty || 0) || !!l.discrepancy_approved !== !!transfer.items[i].discrepancy_approved);
    if (!changed.length) return toast.info("No GRN changes to save");
    setSaving(true);
    try {
      await axios.patch(`${API}/mcf/transfers/${transfer.id}/grn`, {
        expected_updated_at: transfer.updated_at,
        lines: changed.map((l) => ({ line_id: l.line_id, grn_qty: l.grn_qty, discrepancy_approved: l.grn_qty > l.sent_qty && !!l.discrepancy_approved })),
      });
      toast.success(`GRN saved — pending ${fmtQty(t.pending)}, excess ${fmtQty(t.excess)} · ${nextStatus}`);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err, "Could not update GRN"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && !saving && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="mcf-grn-dialog">
        <DialogHeader>
          <DialogTitle>Update GRN · {transfer.invoice_number}</DialogTitle>
          <DialogDescription>Enter the cumulative quantity received so far for each SKU. Status updates automatically: In-Transit (nothing received), Partially Delivered (any short or excess line), Delivered (every line GRN = Sent).</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-sm">
            Sent <b className="font-mono">{fmtQty(t.sent)}</b> · GRN <b className="font-mono" data-testid="mcf-grn-total">{fmtQty(t.grn)}</b> · Pending <b className="font-mono text-amber-700" data-testid="mcf-grn-total-pending">{fmtQty(t.pending)}</b> · Excess <b className="font-mono text-rose-700" data-testid="mcf-grn-total-excess">{fmtQty(t.excess)}</b>
            <span className={`ml-3 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[nextStatus]}`} data-testid="mcf-grn-next-status">{nextStatus}</span>
          </p>
          <Button size="sm" variant="outline" className="border-[#1F8F88] text-[#1F8F88] hover:bg-teal-50 hover:text-[#1F8F88]" onClick={() => setLines((ls) => ls.map((l) => ({ ...l, grn_input: String(l.sent_qty), discrepancy_approved: false })))} data-testid="mcf-grn-receive-all">
            <PackageCheck className="h-4 w-4 mr-1" /> Receive all
          </Button>
        </div>
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>SKU ID</TableHead><TableHead>SKU Name</TableHead>
                <TableHead className="text-right">Sent Units</TableHead><TableHead className="text-right w-[150px]">GRN Units</TableHead><TableHead className="text-right">Pending GRN</TableHead><TableHead className="text-right">Excess GRN</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {view.map((l, idx) => (
                <TableRow key={l.line_id} data-testid={`mcf-grn-row-${idx}`}>
                  <TableCell className="font-mono text-xs">{l.sku_id}</TableCell>
                  <TableCell>{l.sku_name}</TableCell>
                  <TableCell className="text-right font-mono">{fmtQty(l.sent_qty)}</TableCell>
                  <TableCell className="text-right">
                    <Input type="number" min="0" className={`h-8 text-right ${errors[idx] ? "border-rose-400" : ""}`} value={lines[idx].grn_input} onChange={(e) => set(idx, { grn_input: e.target.value })} data-testid={`mcf-grn-input-${idx}`} />
                    {l.grn_qty > l.sent_qty && (
                      <label className="flex items-center justify-end gap-1 mt-1 text-[11px] text-rose-700">
                        <Checkbox checked={!!l.discrepancy_approved} onCheckedChange={(v) => set(idx, { discrepancy_approved: !!v })} data-testid={`mcf-grn-excess-${idx}`} /> Approve excess
                      </label>
                    )}
                    {errors[idx] && l.grn_qty <= l.sent_qty && <p className="text-[11px] text-rose-700 mt-1">{errors[idx]}</p>}
                  </TableCell>
                  <TableCell className={`text-right font-mono font-semibold ${lineGap(l).pending ? "text-amber-700" : "text-emerald-700"}`} data-testid={`mcf-grn-pending-${idx}`}>{fmtQty(lineGap(l).pending)}</TableCell>
                  <TableCell className={`text-right font-mono font-semibold ${lineGap(l).excess ? "text-rose-700" : "text-slate-400"}`} data-testid={`mcf-grn-excess-qty-${idx}`}>{fmtQty(lineGap(l).excess)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="mcf-grn-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#1F8F88] hover:bg-[#197a74] text-white" data-testid="mcf-grn-save">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save GRN
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
